import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { api } from '../services/api';
import { BadgeTypeModel, ParticipantModel } from '../types';
import { ArrowLeft, Printer, Download } from 'lucide-react';

interface ParticipantBadgeViewProps {
  participantIdOrToken: string;
  onBack: () => void;
}

export const ParticipantBadgeView: React.FC<ParticipantBadgeViewProps> = ({
  participantIdOrToken,
  onBack,
}) => {
  const [data, setData] = useState<{
    eventName: string;
    participant: ParticipantModel;
    badgeType?: BadgeTypeModel;
  } | null>(null);
  const [qrUrl, setQrUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const res = await api.getParticipantBadge(participantIdOrToken);
        setData(res);

        // Generate clean high-resolution QR
        const qrData = `${window.location.origin}/q/${res.participant.secureQrToken}`;
        const generated = await QRCode.toDataURL(qrData, {
          width: 300,
          margin: 2,
          color: {
            dark: '#0f172a',
            light: '#ffffff',
          },
        });
        setQrUrl(generated);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [participantIdOrToken]);

  if (loading) {
    return (
      <div className="py-16 text-center text-xs text-slate-400">
        Loading badge...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-16 text-center space-y-3">
        <div className="text-sm font-semibold text-rose-400">Badge Not Found</div>
        <button
          onClick={onBack}
          className="px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg"
        >
          Back
        </button>
      </div>
    );
  }

  const { eventName, participant, badgeType } = data;

  return (
    <div className="max-w-xs mx-auto space-y-4">
      {/* Top back & actions */}
      <div className="flex items-center justify-between text-xs">
        <button
          onClick={onBack}
          className="text-slate-400 hover:text-white inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back</span>
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded text-xs inline-flex items-center gap-1"
          >
            <Printer className="w-3 h-3" />
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* Clean Physical Badge Layout */}
      <div className="rounded-2xl bg-white text-slate-900 border border-slate-200 shadow-xl overflow-hidden print:shadow-none print:border-none">
        {/* Event Header Ribbon */}
        <div
          className="p-4 text-center text-white"
          style={{ backgroundColor: badgeType?.color || '#0f172a' }}
        >
          <div className="text-[10px] font-bold tracking-widest uppercase opacity-80">
            OFFICIAL ACCESS BADGE
          </div>
          <div className="text-sm font-bold tracking-tight truncate mt-0.5">
            {eventName}
          </div>
        </div>

        {/* Participant Details */}
        <div className="p-6 text-center space-y-3">
          {/* Avatar Photo */}
          <img
            src={participant.photoUrl}
            alt={participant.fullName}
            className="w-20 h-20 rounded-full mx-auto bg-slate-100 object-cover border-2 border-slate-200 shadow-sm"
          />

          <div>
            <h2 className="text-lg font-bold tracking-tight text-slate-950">
              {participant.fullName}
            </h2>
            <div
              className="inline-block mt-1 px-3 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider"
              style={{
                backgroundColor: `${badgeType?.color || '#2563eb'}15`,
                color: badgeType?.color || '#2563eb',
              }}
            >
              {badgeType?.name || 'Attendee'}
            </div>
          </div>

          {/* High-Res QR Code */}
          <div className="p-3 bg-slate-50 rounded-xl inline-block border border-slate-200">
            {qrUrl ? (
              <img src={qrUrl} alt="QR Code" className="w-44 h-44 block mx-auto" />
            ) : (
              <div className="w-44 h-44 flex items-center justify-center text-xs text-slate-400">
                Generating QR...
              </div>
            )}
          </div>

          {/* Badge ID */}
          <div className="pt-1">
            <div className="text-xs font-mono font-bold tracking-wider text-slate-800">
              {participant.badgeNumber}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              Scan this code at entrance gates
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
